/**
 * The Run Summary result — a page the optimizer laid out itself.
 *
 * The summary launcher runs the diagnostic, pacing and performance briefs, merges them,
 * and returns `details.layout_html`: a fragment whose SHAPE the model chose (a blocker
 * lead, a table of similar changes, a short all-clear) but whose every fact and control
 * was rendered in Python from the validated result. See
 * campaign-services/campaign_optimizer/dv360_summary_layout.py for that contract.
 *
 * WHY dangerouslySetInnerHTML IS SAFE HERE, AND WHY THAT IS NOT A SENTENCE TO TRUST
 * The fragment is rebuilt server-side from a tag allowlist before it is ever stored:
 * unknown tags unwrapped, script/style/iframe/form subtrees dropped whole, every `on*`
 * handler and href removed, and `<button>` dropped with its subtree so the only apply
 * control that exists is one Python emitted from a validated edit payload. That
 * sanitizing is the load-bearing part — a campaign or deal name flows through the
 * optimizer prompt into the answer, so a line item named `<img src=x onerror=…>` would
 * otherwise be stored XSS with the model as the courier. If that sanitizer is ever
 * bypassed, this component is where it lands.
 *
 * WIRING THE APPLY BUTTONS
 * Python renders `<button data-apply-action="pace_0">`; it cannot render a React handler.
 * So the buttons arrive inert and this component delegates their clicks to the SAME
 * onApplyChanges path the suggestion cards use — the summary result's
 * `details.recommendations` is `details.actions`, index-aligned, so an action id resolves
 * to the backend suggestion index the apply/applied state is already keyed on. One apply
 * path, not two.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

/** Where a page button's action id sits in the backend's suggestion list, or -1. */
function indexOfAction(actions, actionId) {
  if (!Array.isArray(actions) || !actionId) return -1;
  return actions.findIndex((a) => a && a.action_id === actionId);
}

/**
 * Reflect apply state back into markup React does not own.
 *
 * The buttons live inside injected HTML, so they cannot re-render from props — this
 * walks them after every relevant change and sets the label and disabled state directly.
 * Without it a button stays reading "Apply Changes" after the edit was dispatched, which
 * is an invitation to send it twice.
 */
function syncApplyButtons(root, { actions, canApply, isApplied, applyingActionId }) {
  if (!root) return;
  root.querySelectorAll('[data-apply-action]').forEach((button) => {
    const actionId = button.getAttribute('data-apply-action');
    const index = indexOfAction(actions, actionId);
    const application = index >= 0 && typeof isApplied === 'function' && isApplied(index);
    const applied = Boolean(application);
    const applying = applyingActionId === actionId;
    // An action the page names but the result does not carry cannot be applied. The
    // server appends any action the layout missed, so this means the layout invented
    // an id — render it inert rather than wiring a button to nothing.
    const usable = canApply && index >= 0;

    button.disabled = !usable || applied || Boolean(applyingActionId);
    button.style.cursor = button.disabled ? 'not-allowed' : 'pointer';
    button.style.opacity = applyingActionId && !applying && !applied ? 0.6 : 1;
    button.style.backgroundColor = applied ? '#198754' : '';
    button.style.borderColor = applied ? '#198754' : '';
    button.style.color = applied ? '#fff' : '';
    button.style.display = applied ? 'inline-flex' : '';
    button.style.alignItems = applied ? 'center' : '';
    button.style.justifyContent = applied ? 'center' : '';
    button.style.textAlign = applied ? 'center' : '';
    if (applying) button.textContent = 'Applying…';
    else if (applied) {
      const timestamp = application && typeof application === 'object'
        ? application.appliedAt : null;
      button.textContent = timestamp
        ? `Change Applied on ${new Date(timestamp).toLocaleString()}`
        : 'Change Applied';
    }
    else if (!usable) button.textContent = 'Apply in DV360';
    else button.textContent = 'Apply Changes';
  });
}

/**
 * @param {object}   props
 * @param {string}   props.html              details.layout_html, already sanitized server-side
 * @param {Array}    props.actions           details.actions — index-aligned with the
 *                                           backend's suggestion list
 * @param {Function} [props.onApply]         (action, index) => void; omitted makes the
 *                                           page read-only
 * @param {Function} [props.isApplied]       (index) => application match or null
 * @param {string}   [props.applyingActionId] the action currently being dispatched
 */
export default function SummaryOptimizerLayout({
  html,
  actions,
  onApply,
  isApplied,
  applyingActionId = null,
}) {
  const rootRef = useRef(null);
  const canApply = typeof onApply === 'function';

  // Identity for the sync effect. Recomputing on every render would re-walk the DOM on
  // unrelated parent updates; the applied set only moves when one of these does.
  const appliedKey = useMemo(() => {
    if (!Array.isArray(actions) || typeof isApplied !== 'function') return '';
    return actions.map((_, i) => {
      const match = isApplied(i);
      return match && typeof match === 'object' ? String(match.appliedAt || 'applied') : String(Boolean(match));
    }).join('|');
  }, [actions, isApplied]);

  const onClick = useCallback((event) => {
    const button = event.target.closest?.('[data-apply-action]');
    if (!button || !rootRef.current?.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();
    if (!canApply || button.disabled) return;
    const actionId = button.getAttribute('data-apply-action');
    const index = indexOfAction(actions, actionId);
    if (index < 0) return;
    onApply(actions[index], index, actionId);
  }, [actions, canApply, onApply]);

  useEffect(() => {
    syncApplyButtons(rootRef.current, {
      actions, canApply, isApplied, applyingActionId,
    });
  }, [html, actions, canApply, isApplied, applyingActionId, appliedKey]);

  if (typeof html !== 'string' || !html.trim()) return null;

  return (
    <div
      ref={rootRef}
      onClick={onClick}
      className="summary-optimizer-layout"
      // See the module comment: sanitized in Python before it was ever stored.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
